import { useCurrentUser, useUsers } from '@centecomm/portal-service-react';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'Shared/Modal/Modal';
import TermsOfServicePage from 'Core/StaticPages/TermsOfServicePage';
import { CheckBoxWithJSXLabel } from 'Shared/FormFields';
import { Label } from 'Shared/Forms/Label';
import { ScrollToTop } from 'Shared/ScrollToTop';
import PrivacyPolicyPage from 'Core/StaticPages/PrivacyPolicyPage';
import { LoadingContent } from 'Shared/PageElements';
import { globalMessageActionCreators } from 'State';
import { useDispatch } from 'react-redux';
import { UserPermissions, UserTerms, WelcomeFeatureSettings } from '@centecomm/portal-service';
import { PageWrapper } from 'Shared/PageWrapper';
import ExpandableSection from 'Shared/ExpandableSection/ExpandableSection';
import parse from 'html-react-parser';
import styles from './WelcomeModal.module.css';

// Lazy import for Account component to prevent bundling in main chunk
const EditAccountLanguage = React.lazy(() =>
    import('Features/Account/shared').then((module) => ({
        default: module.EditAccountLanguage,
    })),
);

interface WelcomeModalProps {
    isLoading: boolean;
    userTerms: UserTerms;
    userSettings?: WelcomeFeatureSettings | undefined;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ isLoading, userTerms, userSettings }: WelcomeModalProps) => {
    const { t } = useTranslation(['businessFeature']);
    const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
    const [acceptPrivacy, setAcceptPrivacy] = useState<boolean>(false);

    const [currentSequenceIndex, setCurrentSequenceIndex] = useState(0);

    const { currentUserId: userId, isTenant, hasPermission } = useCurrentUser();
    const { updateUserTerms } = useUsers();
    const dispatch = useDispatch();

    const isAdminPermission = hasPermission(UserPermissions.CreateTenant);

    const readyToSave = useMemo(() => acceptTerms && acceptPrivacy, [acceptTerms, acceptPrivacy]);

    useEffect(() => {
        (async () => {
            if (readyToSave) {
                try {
                    await updateUserTerms(userId as number);
                } catch (error) {
                    dispatch(
                        globalMessageActionCreators.addErrorMessage(
                            t('businessFeature:welcome.welcomeModal.errors.unableToUpdateSubscription'),
                            error,
                        ),
                    );
                }
            }
        })();
    }, [readyToSave]);

    const AcceptCheckBoxWrapper = ({
        labelMessage,
        isTerms,
        isPrivacy,
    }: {
        labelMessage: string;
        isTerms?: boolean;
        isPrivacy?: boolean;
    }) => (
        <div style={{ padding: 20 }}>
            <CheckBoxWithJSXLabel
                id="check-1"
                label={<Label id="label-1" label={labelMessage} />}
                name="check-1"
                checkedByDefault={false}
                handler={() => {
                    setCurrentSequenceIndex(currentSequenceIndex + 1);

                    if (isTerms) setAcceptTerms(!acceptTerms);

                    if (isPrivacy) setAcceptPrivacy(!acceptPrivacy);
                }}
                required
            />
        </div>
    );

    const ModalTermsWrapper = ({
        children,
        customTerms,
        pageTitle,
    }: {
        children?: React.ReactNode;
        customTerms?: string | undefined;
        pageTitle?: string;
    }) => (
        <Modal hideHeader>
            <div style={{ width: '100vw', maxWidth: '40rem', height: '70vh', padding: 20 }}>
                <ScrollToTop />
                {!customTerms ? (
                    <>
                        <div>{children}</div>
                    </>
                ) : (
                    <>
                        <PageWrapper pageTitle={pageTitle}>
                            <ExpandableSection title={pageTitle}>
                                <section className={styles.CustomTermsPage}>
                                    <div>{parse(customTerms ?? '')}</div>
                                </section>
                            </ExpandableSection>
                        </PageWrapper>
                        {children}
                    </>
                )}
            </div>
        </Modal>
    );

    const AccountLanguagePreferences = () => (
        <Modal hideHeader>
            <div style={{ width: '100vw', maxWidth: '40rem', height: '70vh', padding: 20 }}>
                <PageWrapper>
                    <section>
                        {t('businessFeature:welcome.welcomeModal.sections.accountLanguage.description')}
                    </section>
                    <React.Suspense fallback={<LoadingContent />}>
                        <EditAccountLanguage
                            onSave={() => {
                                setCurrentSequenceIndex(currentSequenceIndex + 1);
                            }}
                            hideCancel
                        />
                    </React.Suspense>
                </PageWrapper>
            </div>
        </Modal>
    );

    const TermsOfService = () => (
        <ModalTermsWrapper>
            <TermsOfServicePage />
            <AcceptCheckBoxWrapper isTerms labelMessage={t('businessFeature:welcome.welcomeModal.sections.terms.label')} />
        </ModalTermsWrapper>
    );

    const PrivacyPolicy = () => (
        <ModalTermsWrapper>
            <PrivacyPolicyPage />
            <AcceptCheckBoxWrapper
                isPrivacy
                labelMessage={t('businessFeature:welcome.welcomeModal.sections.privacy.label')}
            />
        </ModalTermsWrapper>
    );

    const CustomTerms = () => (
        <ModalTermsWrapper
            customTerms={userTerms.customTermsAndConditionsText}
            pageTitle={t('businessFeature:welcome.welcomeModal.sections.customTerms.title')}
        >
            <AcceptCheckBoxWrapper labelMessage={t('businessFeature:welcome.welcomeModal.sections.customTerms.label')} />
        </ModalTermsWrapper>
    );

    const LeaseTermAcknowledgement = () => (
        <ModalTermsWrapper
            customTerms={userSettings?.leaseTermAcknowledgementText}
            pageTitle={t('businessFeature:welcome.welcomeModal.sections.leaseTermAcknowledgement.title')}
        >
            <AcceptCheckBoxWrapper
                labelMessage={t('businessFeature:welcome.welcomeModal.sections.leaseTermAcknowledgement.label')}
            />
        </ModalTermsWrapper>
    );

    const UtilityTransferAcknowledgement = () => (
        <ModalTermsWrapper
            customTerms={userSettings?.utilityTransferAcknowledgementText}
            pageTitle={t('businessFeature:welcome.welcomeModal.sections.utilityTransferAcknowledgement.title')}
        >
            <AcceptCheckBoxWrapper
                labelMessage={t('businessFeature:welcome.welcomeModal.sections.utilityTransferAcknowledgement.label')}
            />
        </ModalTermsWrapper>
    );

    const KeyAndAccessAcknowledgement = () => (
        <ModalTermsWrapper
            customTerms={userSettings?.keyAndAccessAcknowledgementText}
            pageTitle={t('businessFeature:welcome.welcomeModal.sections.keyAndAccessAcknowledgement.title')}
        >
            <AcceptCheckBoxWrapper
                labelMessage={t('businessFeature:welcome.welcomeModal.sections.keyAndAccessAcknowledgement.label')}
            />
        </ModalTermsWrapper>
    );

    const RulesAndRegulationsAcknowledgement = () => (
        <ModalTermsWrapper
            customTerms={userSettings?.rulesAndRegulationsAcknowledgementText}
            pageTitle={t('businessFeature:welcome.welcomeModal.sections.rulesAndRegulationsAcknowledgement.title')}
        >
            <AcceptCheckBoxWrapper
                labelMessage={t('businessFeature:welcome.welcomeModal.sections.rulesAndRegulationsAcknowledgement.label')}
            />
        </ModalTermsWrapper>
    );

    const RentPaymentAcknowledgement = () => (
        <ModalTermsWrapper
            customTerms={userSettings?.rentPaymentAcknowledgementText}
            pageTitle={t('businessFeature:welcome.welcomeModal.sections.rentPaymentAcknowledgement.title')}
        >
            <AcceptCheckBoxWrapper
                labelMessage={t('businessFeature:welcome.welcomeModal.sections.rentPaymentAcknowledgement.label')}
            />
        </ModalTermsWrapper>
    );

    const ComplianceSubmissionAcknowledgement = () => (
        <ModalTermsWrapper
            customTerms={userSettings?.complianceSubmissionAcknowledgementText}
            pageTitle={t('businessFeature:welcome.welcomeModal.sections.complianceSubmissionAcknowledgement.title')}
        >
            <AcceptCheckBoxWrapper
                labelMessage={t('businessFeature:welcome.welcomeModal.sections.complianceSubmissionAcknowledgement.label')}
            />
        </ModalTermsWrapper>
    );

    const EndSequence = () => <></>;

    const ModalSequence: Array<() => JSX.Element> = [];

    if (userTerms.termsAcceptedOn === null) {
        ModalSequence.push(AccountLanguagePreferences);
        ModalSequence.push(TermsOfService);
        ModalSequence.push(PrivacyPolicy);

        if (userTerms && userTerms.useCustomTermsAndConditions) ModalSequence.push(CustomTerms);

        if (isTenant && isAdminPermission) {
            const onboardConfig = userSettings?.onBoardingConfiguration;

            if (onboardConfig?.leaseTermAcknowledgement?.enabled ?? false) ModalSequence.push(LeaseTermAcknowledgement);

            if (onboardConfig?.utilityTransferAcknowledgement?.enabled ?? false)
                ModalSequence.push(UtilityTransferAcknowledgement);

            if (onboardConfig?.keyAndAccessAcknowledgement?.enabled ?? false)
                ModalSequence.push(KeyAndAccessAcknowledgement);

            if (onboardConfig?.rulesAndRegulationsAcknowledgement?.enabled ?? false)
                ModalSequence.push(RulesAndRegulationsAcknowledgement);

            if (onboardConfig?.rentPaymentAcknowledgement?.enabled ?? false) ModalSequence.push(RentPaymentAcknowledgement);

            if (onboardConfig?.complianceSubmissionAcknowledgement?.enabled ?? false)
                ModalSequence.push(ComplianceSubmissionAcknowledgement);
        }
    }

    ModalSequence.push(EndSequence);

    return (
        <>
            {isLoading && (
                <Modal hideHeader>
                    <LoadingContent />
                </Modal>
            )}

            <>{ModalSequence[currentSequenceIndex]()}</>
        </>
    );
};

export default WelcomeModal;
